/**
 * ClubHeader Component
 * Displays club identity with hero banner and branding
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaCog, 
  FaBell, 
  FaGlobe, 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaYoutube,
  FaMapMarkerAlt
} from 'react-icons/fa';
import styles from './ClubHeader.module.css';

// Helper to extract URL from banner object
const getBannerUrl = (bannerImage) => {
  if (!bannerImage) return null;
  
  // If it's already a string URL
  if (typeof bannerImage === 'string') return bannerImage;
  
  // Try different possible structures
  if (bannerImage.transformedUrls?.full) return bannerImage.transformedUrls.full;
  if (bannerImage.transformedUrls?.banner) return bannerImage.transformedUrls.banner;
  if (bannerImage.url) return bannerImage.url;
  if (bannerImage.secure_url) return bannerImage.secure_url;
  if (bannerImage.cloudinary?.url) return bannerImage.cloudinary.url;
  if (bannerImage.cloudinary?.secure_url) return bannerImage.cloudinary.secure_url;
  
  return null;
};

const ClubHeader = ({ 
  club, 
  programCounts = {}, 
  canManage = false,
  onManageClick,
  onNotificationsClick 
}) => {
  const navigate = useNavigate();
  
  if (!club) return null;

  const totalPrograms = programCounts.total || 0;
  const bannerUrl = getBannerUrl(club.bannerImage);
  const primaryColor = club.primaryColor || '#A4EB3F';

  // Handle club name click - navigate to landing page
  const handleClubNameClick = () => {
    navigate(`/club/${club._id}`);
  };

  // Check if any social links exist
  const hasSocialLinks = club.socialLinks && Object.values(club.socialLinks).some(v => v);

  // Social link config
  const socialConfig = [
    { key: 'website', icon: FaGlobe, color: '#A4EB3F' },
    { key: 'facebook', icon: FaFacebook, color: '#1877F2' },
    { key: 'instagram', icon: FaInstagram, color: '#E4405F' },
    { key: 'twitter', icon: FaTwitter, color: '#1DA1F2' },
    { key: 'youtube', icon: FaYoutube, color: '#FF0000' },
  ];

  // Format location string
  const locationString = [club.city, club.state].filter(Boolean).join(', ');

  return (
    <div 
      className={styles.header}
      style={{ '--club-color': primaryColor }}
    >
      {/* Hero Banner - Always show, even without image */}
      <div className={styles.heroBanner}>
        {bannerUrl ? (
          <>
            <img 
              src={bannerUrl} 
              alt={`${club.name} banner`}
              className={styles.bannerImage}
            />
            <div className={styles.bannerOverlay} />
          </>
        ) : (
          <div className={styles.defaultBanner} style={{ '--club-color': primaryColor }} />
        )}
      </div>

      <div className={styles.headerContent}>
        {/* Club Identity */}
        <div className={styles.clubIdentity}>
          <div className={styles.logoWrapper}>
            {club.clubLogo ? (
              <img 
                src={typeof club.clubLogo === 'string' ? club.clubLogo : club.clubLogo.url} 
                alt={`${club.name} logo`} 
                className={styles.clubLogo}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className={styles.clubLogoPlaceholder}>
                <FaUsers className={styles.placeholderIcon} />
              </div>
            )}
          </div>
          
          <div className={styles.clubInfo}>
            {/* Club Name - Primary focus */}
            <h1 
              className={styles.clubName} 
              onClick={handleClubNameClick}
              title="View club landing page"
            >
              {club.name}
            </h1>
            
            {/* Tagline */}
            {club.tagline && (
              <p className={styles.tagline}>{club.tagline}</p>
            )}
            
            {/* Description (only if no tagline) */}
            {!club.tagline && club.description && (
              <p className={styles.clubDescription}>{club.description}</p>
            )}

            {/* Meta: Sport + Location */}
            <div className={styles.clubMeta}>
              <span className={styles.sportBadge}>{club.sport || 'Sports'}</span>
              {locationString && (
                <span className={styles.locationBadge}>
                  <FaMapMarkerAlt />
                  {locationString}
                </span>
              )}
            </div>

            {/* Social Links */}
            {hasSocialLinks && (
              <div className={styles.socialLinks}>
                {socialConfig.map(({ key, icon: Icon, color }) => 
                  club.socialLinks?.[key] && (
                    <a
                      key={key}
                      href={club.socialLinks[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      style={{ '--social-color': color }}
                      title={key.charAt(0).toUpperCase() + key.slice(1)}
                    >
                      <Icon />
                    </a>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Stats + Actions */}
        <div className={styles.rightSection}>
          {/* Quick Stats */}
          <div className={styles.quickStats}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{totalPrograms}</span>
              <span className={styles.statLabel}>Programs</span>
            </div>
            
            {programCounts.byCategory && Object.entries(programCounts.byCategory)
              .filter(([key, value]) => key !== 'all' && value > 0)
              .slice(0, 2) // Show max 2 categories for cleaner look
              .map(([category, count]) => (
                <div key={category} className={styles.statItem}>
                  <span className={styles.statNumber}>{count}</span>
                  <span className={styles.statLabel}>{category}</span>
                </div>
              ))
            }
          </div>

          {/* Actions */}
          {canManage && (
            <div className={styles.headerActions}>
              {onNotificationsClick && (
                <button 
                  className={styles.actionButton}
                  onClick={onNotificationsClick}
                  title="Manage Notifications"
                >
                  <FaBell />
                </button>
              )}
              {onManageClick && (
                <button 
                  className={styles.actionButton}
                  onClick={onManageClick}
                  title="Club Settings"
                >
                  <FaCog />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubHeader;










